<template>
  <div
    class="w-full h-full flex flex-col relative animate-fade-in"
    :class="theme.text"
  >
    <div class="shrink-0 h-12 flex items-center justify-between px-4 z-10">
      <button @click="$emit('back')">
        <icon-base
          name="ChevronLeft"
          class="w-6 h-6 opacity-60 hover:opacity-100"
        ></icon-base>
      </button>
      <span class="font-bold">{{ t.member_title }}</span>
      <div class="w-6"></div>
    </div>

    <div class="flex-1 overflow-y-auto px-4 pb-4">
      <div class="text-center mb-6 mt-1">
        <div
          class="inline-block p-2 rounded-xl bg-gradient-to-tr from-amber-400 to-orange-500 shadow-lg shadow-orange-500/30 mb-2"
        >
          <icon-base name="Crown" class="w-6 h-6 text-white"></icon-base>
        </div>
        <h2 class="font-bold mb-1" :class="isWatch ? 'text-lg' : 'text-2xl'">
          {{ t.unlock_title }}
        </h2>
        <p v-if="!isWatch" class="opacity-60 text-xs">{{ t.unlock_desc }}</p>
      </div>

      <div class="flex flex-col gap-3 mb-6">
        <div v-if="loadingGoods" class="text-center py-4 opacity-50">
          <icon-base
            name="Refresh"
            class="w-6 h-6 animate-spin mx-auto mb-2"
          ></icon-base>
          <span class="text-xs">Loading plans...</span>
        </div>

        <template v-else>
          <button
            v-for="item in goodsList"
            :key="item.id"
            @click="selectedId = item.id"
            class="w-full p-3 rounded-xl border-2 transition-all relative flex items-center justify-between"
            :class="
              selectedId === item.id
                ? 'border-amber-500 bg-amber-500/5'
                : theme.isDark
                ? 'border-white/10'
                : 'border-black/5'
            "
          >
            <div
              v-if="item.id === 2"
              class="absolute -top-2 right-2 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm"
            >
              {{ t.recommend || "RECOMMEND" }}
            </div>

            <div class="flex items-center gap-3">
              <div
                class="w-4 h-4 rounded-full border-2 flex items-center justify-center"
                :class="
                  selectedId === item.id
                    ? 'border-amber-500'
                    : 'border-gray-400'
                "
              >
                <div
                  v-if="selectedId === item.id"
                  class="w-2 h-2 rounded-full bg-amber-500"
                ></div>
              </div>
              <div class="text-left">
                <div class="font-bold text-sm">{{ item.name }}</div>
                <div
                  v-if="item.product_description"
                  class="text-[9px] text-amber-500 font-bold"
                >
                  {{ item.product_description }}
                </div>
              </div>
            </div>
            <div class="text-right">
              <div class="font-bold text-sm">
                ¥{{ item.product_price }}
                <span class="text-[10px] font-normal opacity-50">
                  {{
                    item.name.includes("月")
                      ? "/M"
                      : item.name.includes("年")
                      ? "/Y"
                      : ""
                  }}
                </span>
              </div>
            </div>
          </button>
        </template>
      </div>

      <div
        class="rounded-xl p-4 mb-4"
        :class="theme.isDark ? 'bg-white/5' : 'bg-black/5'"
      >
        <div
          class="text-[10px] font-bold opacity-40 uppercase tracking-wider mb-3"
        >
          {{ t.privilege }}
        </div>
        <div class="space-y-3">
          <div
            v-for="(priv, i) in currentPrivileges"
            :key="i"
            class="flex gap-3"
          >
            <div class="mt-0.5">
              <icon-base
                name="Check"
                class="w-3 h-3 text-amber-500"
              ></icon-base>
            </div>
            <div>
              <div class="text-xs font-bold">{{ priv.t }}</div>
              <div v-if="!isWatch" class="text-[10px] opacity-50">
                {{ priv.d }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="p-4 border-t safe-bottom"
      :class="
        theme.isDark
          ? 'border-white/10 bg-[#0f1115]'
          : 'border-black/5 bg-white'
      "
    >
      <button
        @click="openPaymentModal"
        :disabled="loading || !selectedId"
        class="w-full py-3 rounded-xl bg-gradient-to-r from-amber-400 to-orange-600 text-white font-bold text-base shadow-lg shadow-orange-500/20 active:scale-95 transition-all flex items-center justify-center gap-2"
      >
        <span v-if="!loading">{{ t.pay_btn }} {{ priceDisplay }}</span>
        <icon-base
          v-else
          name="Refresh"
          class="w-4 h-4 animate-spin"
        ></icon-base>
      </button>
      <div v-if="!isWatch" class="text-[9px] text-center opacity-40 mt-2">
        {{ t.pay_agreement }}
      </div>
    </div>

    <div
      v-if="showPaymentModal"
      class="absolute inset-0 z-50 flex items-end justify-center sm:items-center bg-black/60 backdrop-blur-sm animate-fade-in"
    >
      <div
        class="w-full max-w-sm p-6 rounded-t-2xl sm:rounded-2xl shadow-2xl relative"
        :class="
          theme.isDark ? 'bg-[#1a1c20] text-white' : 'bg-white text-black'
        "
      >
        <button
          @click="showPaymentModal = false"
          class="absolute top-4 right-4 opacity-50 hover:opacity-100"
        >
          <icon-base name="Close" class="w-5 h-5" />
        </button>

        <h3 class="text-lg font-bold mb-6 text-center">选择支付方式</h3>

        <div class="flex flex-col gap-3">
          <button
            @click="confirmPay('WeChatPay')"
            class="w-full py-3.5 px-4 rounded-xl flex items-center justify-between border-2 transition-all hover:bg-green-500/5 active:scale-95"
            :class="
              theme.isDark
                ? 'border-white/10 hover:border-green-500'
                : 'border-black/5 hover:border-green-500'
            "
          >
            <div class="flex items-center gap-3">
              <div
                class="w-8 h-8 rounded-full bg-[#07C160] flex items-center justify-center text-white"
              >
                <span class="font-bold text-xs">微信</span>
              </div>
              <span class="font-bold">微信支付</span>
            </div>
            <icon-base name="ChevronRight" class="w-4 h-4 opacity-40" />
          </button>

          <button
            v-if="paymentProduct !== 'JSAPI'"
            @click="confirmPay('Alipay')"
            class="w-full py-3.5 px-4 rounded-xl flex items-center justify-between border-2 transition-all hover:bg-blue-500/5 active:scale-95"
            :class="
              theme.isDark
                ? 'border-white/10 hover:border-blue-500'
                : 'border-black/5 hover:border-blue-500'
            "
          >
            <div class="flex items-center gap-3">
              <div
                class="w-8 h-8 rounded-full bg-[#1677FF] flex items-center justify-center text-white"
              >
                <span class="font-bold text-xs">支付宝</span>
              </div>
              <span class="font-bold">支付宝</span>
            </div>
            <icon-base name="ChevronRight" class="w-4 h-4 opacity-40" />
          </button>
        </div>

        <div class="mt-6 text-center text-xs opacity-40">
          支付环境: {{ paymentProduct }}
        </div>
      </div>
    </div>

    <div
      v-if="showQrModal"
      class="absolute inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in"
    >
      <div
        class="w-full max-w-xs p-6 rounded-2xl shadow-2xl relative flex flex-col items-center gap-4 animate-scale-in"
        :class="
          theme.isDark ? 'bg-[#1a1c20] text-white' : 'bg-white text-black'
        "
      >
        <button
          @click="showQrModal = false"
          class="absolute top-4 right-4 opacity-50 hover:opacity-100"
        >
          <icon-base name="Close" class="w-5 h-5" />
        </button>

        <h3 class="text-lg font-bold">扫码支付</h3>

        <div class="bg-white p-2 rounded-xl">
          <qr-code :text="qrCodeText" :size="200" />
        </div>

        <div class="text-xs opacity-50 text-center">
          请使用微信扫一扫完成支付
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getGoodsList, createOrder, orderState } from "@/api/index";
import QrCode from "./QrCode.vue";

export default {
  components: { QrCode },
  props: ["theme", "t", "device"],
  data() {
    return {
      loading: false,
      loadingGoods: true,
      goodsList: [],
      selectedId: null,
      showPaymentModal: false,
      paymentProduct: "NATIVE",

      showQrModal: false,
      qrCodeText: "",
    };
  },
  computed: {
    isWatch() {
      return this.device === "WATCH";
    },
    selectedItem() {
      return this.goodsList.find((i) => i.id === this.selectedId);
    },
    priceDisplay() {
      return this.selectedItem ? `¥${this.selectedItem.product_price}` : "";
    },
    currentPrivileges() {
      if (!this.selectedItem?.remark) return this.t.privileges || [];
      const lines = this.selectedItem.remark
        .split(/\r?\n/)
        .filter((line) => line.trim() !== "");
      const list = [];
      for (let i = 0; i < lines.length; i += 2) {
        list.push({
          t: lines[i],
          d: lines[i + 1] || "",
        });
      }
      return list;
    },
  },
  created() {
    this.detectEnvironment();
  },
  async mounted() {
    await this.fetchGoods();
  },
  methods: {
    detectEnvironment() {
      const ua = navigator.userAgent.toLowerCase();
      const isWeChat = ua.indexOf("micromessenger") !== -1;
      const isMobile =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
          ua
        );

      if (isWeChat) {
        this.paymentProduct = "JSAPI";
      } else if (isMobile) {
        this.paymentProduct = "H5";
      } else {
        this.paymentProduct = "NATIVE";
      }
      console.log("Payment Environment Detected:", this.paymentProduct);
    },

    async fetchGoods() {
      this.loadingGoods = true;
      try {
        const res = await getGoodsList();
        this.goodsList = Array.isArray(res) ? res : res.data || [];
        const def = this.goodsList.find((i) => i.id === 3) || this.goodsList[0];
        if (def) this.selectedId = def.id;
      } catch (e) {
        console.error("Fetch goods failed", e);
      } finally {
        this.loadingGoods = false;
      }
    },

    openPaymentModal() {
      if (!this.selectedId) return;
      this.showPaymentModal = true;
    },

    async confirmPay(paymentWay) {
      this.showPaymentModal = false;
      this.loading = true;

      const orderData = {
        payment_way: paymentWay,
        payment_product: this.paymentProduct,
        goods_id: this.selectedId,
      };

      try {
        console.log("Creating Order:", orderData);
        // 接口返回结构：{ code: 0, data: { order: "url", outTradeNo: "xxx" }, msg: "..." }
        const res = await createOrder(orderData);
        console.log("Order Created:", res);

        // 获取关键数据（兼容处理：根据request配置 res 可能直接是 data 或包含 data）
        const data = res.data || res;
        const link = data.order;
        const outTradeNo = data.outTradeNo;

        // 【H5环境】直接跳转
        if (this.paymentProduct === "H5") {
          window.location.href = link;
          return;
        }

        // 【NATIVE环境 + 微信支付】
        if (this.paymentProduct === "NATIVE" && paymentWay === "WeChatPay") {
          this.qrCodeText = link;
          this.showQrModal = true;

          // 延时2秒后开始轮询
          setTimeout(() => {
            this.pollOrder(outTradeNo);
          }, 2000);

          return;
        }

        // 其他情况（如支付宝 NATIVE，通常也是打开链接）
        if (link && link.startsWith("http")) {
          window.location.href = link;
          // window.open(link, "_blank");
        }
      } catch (error) {
        console.error("Pay failed:", error);
        alert("创建订单失败，请稍后重试");
        this.loading = false;
      }
    },

    // 【新增】轮询订单状态
    async pollOrder(outTradeNo) {
      const maxRetries = 30;
      let count = 0;

      while (count < maxRetries) {
        // 如果用户手动关闭了二维码弹窗，停止轮询
        if (!this.showQrModal) return;

        try {
          // 调用查询接口
          const res = await orderState(outTradeNo);
          // 假设接口返回 { code: 0, data: { trade_state: "..." } }
          const stateData = res.data || res;

          console.log(`Polling ${count + 1}/${maxRetries}:`, stateData);

          // 检查状态 (SUCCESS 为支付成功)
          if (stateData.trade_state === "SUCCESS") {
            this.$emit("success");
            this.showQrModal = false;
            this.loading = false;
            return;
          }
        } catch (e) {
          console.error("Poll order status failed", e);
        }

        count++;
        // 间隔 2 秒
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      // 超过次数仍未支付
      if (this.showQrModal) {
        this.showQrModal = false;
        alert("订单未支付");
      }

      this.loading = false;
    },
  },
};
</script>
